import { useEffect, useRef, RefObject } from 'react';

// Focusable candidates, in DOM order. `:not([tabindex="-1"])` keeps
// programmatic-only focus targets (e.g. <main id="main-content">) out of the
// Tab cycle, matching what the browser itself does.
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button',
  'input',
  'select',
  'textarea',
  '[tabindex]'
].map(sel => `${sel}:not([tabindex="-1"])`).join(', ');

interface FocusableElement extends HTMLElement {
  disabled?: boolean;
}

/**
 * Returns the currently focusable descendants of `container`, in DOM order.
 *
 * This is deliberately re-queried on every Tab rather than captured once when
 * the trap is installed: the sidebar drawer this guards mounts and unmounts
 * children as sections expand (framer-motion's AnimatePresence, the user
 * dropdown, the course search box), so a list snapshotted at open time goes
 * stale within one interaction and the trap starts wrapping at the wrong
 * element.
 */
const getFocusable = (container: HTMLElement): FocusableElement[] =>
  Array.from(container.querySelectorAll<FocusableElement>(FOCUSABLE_SELECTOR)).filter(
    el => !el.disabled && el.getAttribute('aria-hidden') !== 'true'
  );

/**
 * Traps keyboard focus inside `containerRef` while `active` is true.
 *
 * On activation the previously focused element is remembered and the first
 * focusable element inside the container is focused; on deactivation focus is
 * restored to whatever had it before (so closing the drawer with Escape puts
 * the user back on the hamburger button rather than at the top of the page).
 *
 * `active` must be false whenever the container is not behaving as a modal
 * overlay. The sidebar is open by default on desktop, where the page content
 * beside it is still reachable -- trapping there would produce exactly the
 * keyboard trap this hook exists to prevent, so callers gate `active` on the
 * mobile breakpoint.
 *
 * @param containerRef element to trap within
 * @param active whether the trap is currently installed
 * @param onEscape called when Escape is pressed inside the trap
 */
export const useFocusTrap = (
  containerRef: RefObject<HTMLElement>,
  active: boolean,
  onEscape?: () => void
) => {
  // Held in a ref so the keydown listener always sees the latest callback
  // without having to tear down and reinstall the listener on every render.
  const onEscapeRef = useRef(onEscape);
  onEscapeRef.current = onEscape;

  useEffect(() => {
    const container = containerRef.current;
    if (!active || !container) return undefined;

    // `document.activeElement` is typed as the generic DOM `Element`, which
    // does not carry `.focus()` (SVGElement, for instance, doesn't have
    // one) -- the runtime `typeof ... === 'function'` check below is what
    // makes this safe, same as the pre-conversion JS.
    const previouslyFocused = document.activeElement as (Element & { focus?: () => void }) | null;

    const focusable = getFocusable(container);
    if (focusable.length > 0) {
      focusable[0].focus();
    } else {
      // Nothing focusable inside yet; make the container itself the focus
      // holder so Tab has somewhere inside the trap to start from.
      container.setAttribute('tabindex', '-1');
      container.focus();
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onEscapeRef.current?.();
        return;
      }

      if (event.key !== 'Tab') return;

      const current = getFocusable(container);
      if (current.length === 0) {
        // Nothing to move to -- keep focus where it is rather than letting
        // Tab escape to the page behind the overlay.
        event.preventDefault();
        return;
      }

      const first = current[0];
      const last = current[current.length - 1];
      const activeEl = document.activeElement;

      if (event.shiftKey) {
        if (activeEl === first || !container.contains(activeEl)) {
          event.preventDefault();
          last.focus();
        }
      } else if (activeEl === last || !container.contains(activeEl)) {
        event.preventDefault();
        first.focus();
      }
    };

    // Listening on document (capture) rather than on the container: focus can
    // legitimately sit outside the container for a moment, and a listener
    // bound to the container would never see the keydown that should pull it
    // back in.
    document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus();
      }
    };
  }, [containerRef, active]);
};

export default useFocusTrap;
